/**
 * /subscribe — Hard subscription gate landing for blocked practitioners.
 *
 * Triggered when:
 *   • Backend returns 403 { detail.code === "subscription_required" }
 *   • User clicks "Subscribe" from the Practitioner dashboard
 *
 * Flow:
 *   1. Load /api/me/billing (allowlisted) → check if already active
 *   2. Show plans (Lite $49 / Pro $149)
 *   3. POST /api/billing/checkout { plan_key } → redirect to Square invoice URL
 *
 * NOTE: When backend has billing_configured=false, the UI still renders
 *       the plans (so the user can see what they'd get) but the start
 *       buttons surface a helpful error.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, CreditCard, Lock, Clock, ExternalLink,
  ShieldCheck, Sparkles, RefreshCcw, LogOut,
} from "lucide-react";
import { adminAuth } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${adminAuth.getToken()}` } });

export default function Subscribe() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/me/billing`, auth());
      setMe(r.data || null);
    } catch (e) {
      if (e.response?.status === 401) {
        navigate("/admin/login?next=/subscribe", { replace: true });
        return;
      }
      toast.error("Could not load billing status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!adminAuth.getToken()) {
      navigate("/admin/login?next=/subscribe", { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line
  }, []);

  const start = async (plan_key) => {
    setStarting(plan_key);
    try {
      const r = await axios.post(`${API}/billing/checkout`, { plan_key }, auth());
      if (r.data?.invoice_url) {
        toast.success("Redirecting to Square to complete payment…");
        window.location.href = r.data.invoice_url;
        return;
      }
      toast.info("Subscription created. Check your email for the Square invoice link.");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : detail?.message || "Could not start subscription";
      toast.error(msg);
    } finally {
      setStarting("");
    }
  };

  const signOut = () => { adminAuth.clear(); navigate("/admin/login", { replace: true }); };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="subscribe-loading">
        <Loader2 className="w-5 h-5 animate-spin text-purple-900" />
      </div>
    );
  }

  const sub = me?.subscription || null;
  const isSuper = !!me?.is_super_admin;
  const billingConfigured = !!me?.billing_configured;
  const isActive = sub?.status === "active" && sub?.access_unlocked;
  const isPending = sub?.status === "square_pending";
  const pendingUrl = isPending ? (sub?.square_payment_url || "") : "";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="subscribe-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="inline-flex items-center gap-2 text-purple-900">
            <Sparkles className="w-4 h-4" />
            <span className="font-display tracking-wide">KCIS Practitioner Subscription</span>
          </div>
          <button onClick={signOut} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="subscribe-signout">
            <LogOut className="w-3 h-3" />Sign out
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {/* Hard-gate explainer card */}
        <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5 mb-6" data-testid="subscribe-gate-card">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
            <div>
              <h1 className="font-display text-xl text-amber-900">
                {isActive ? "Subscription Active" :
                  isPending ? "Payment Pending" :
                  "Your subscription is not active"}
              </h1>
              <p className="text-sm text-amber-800 mt-1">
                {isActive
                  ? `You're subscribed to ${sub.plan_name}. Access to all KCIS practitioner features is unlocked.`
                  : isPending
                    ? `Complete payment for ${sub?.plan_name} ($${sub?.monthly_amount_usd}/mo) via the Square invoice to unlock practitioner access.`
                    : "Practitioner access is locked. Choose a plan below to subscribe via Square. Your subscription auto-renews monthly until cancelled."}
              </p>
              {isPending && pendingUrl && (
                <a href={pendingUrl} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-amber-700 hover:bg-amber-800 text-white h-10 px-4 text-sm font-medium" data-testid="subscribe-pay-pending" target="_blank" rel="noopener noreferrer">
                  <CreditCard className="w-4 h-4" />Complete Square Payment
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          </div>
        </section>

        {isSuper && (
          <div className="rounded-xl border border-purple-200 bg-purple-50 p-3 mb-6 text-xs text-purple-900" data-testid="subscribe-super-banner">
            You're signed in as <b>super_admin</b>. Super admins do not subscribe — you have full platform access.
          </div>
        )}

        {!billingConfigured && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 mb-6 text-xs text-rose-900" data-testid="subscribe-unconfigured-banner">
            Billing is not yet configured on this environment. Contact your administrator
            (set <code className="font-mono">SQUARE_LITE_PLAN_ID</code> / <code className="font-mono">SQUARE_PRO_PLAN_ID</code> and reload).
          </div>
        )}

        {/* Plans grid */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="subscribe-plans">
          {(me?.plans || []).map((p) => {
            const current = sub?.plan_key === p.key && isActive;
            const pendingThis = isPending && sub?.plan_key === p.key;
            return (
              <div
                key={p.key}
                className={`rounded-2xl border p-6 transition ${current ? "border-purple-900 bg-purple-50/40" : "border-slate-200 bg-white hover:border-purple-300"}`}
                data-testid={`subscribe-plan-${p.key}`}
              >
                <div className="text-[10px] uppercase tracking-wide text-purple-900 mb-2">
                  {p.key === "pro" ? "Most Popular" : "\u00A0"}
                </div>
                <div className="font-display text-2xl text-slate-900">{p.name}</div>
                <div className="mt-2 text-3xl font-display text-slate-900">
                  ${p.monthly_amount_usd}<span className="text-sm text-slate-500">/mo</span>
                </div>
                <ul className="mt-5 space-y-1.5 text-sm text-slate-700">
                  {(p.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  {current ? (
                    <div className="rounded-xl bg-purple-100 text-purple-900 h-11 inline-flex items-center px-4 text-sm w-full justify-center" data-testid={`subscribe-current-${p.key}`}>
                      <ShieldCheck className="w-4 h-4 mr-2" />Active Plan
                    </div>
                  ) : pendingThis ? (
                    <div className="rounded-xl bg-amber-100 text-amber-900 h-11 inline-flex items-center px-4 text-sm w-full justify-center gap-1.5">
                      <Clock className="w-4 h-4" />Awaiting Payment
                    </div>
                  ) : (
                    <button
                      onClick={() => start(p.key)}
                      disabled={starting === p.key || isSuper}
                      className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white h-11 w-full text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                      data-testid={`subscribe-start-${p.key}`}
                    >
                      {starting === p.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                      Subscribe — ${p.monthly_amount_usd}/mo
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </section>

        <div className="mt-6 text-xs text-slate-500 flex items-center justify-between">
          <button onClick={load} className="inline-flex items-center gap-1 hover:text-slate-800" data-testid="subscribe-refresh">
            <RefreshCcw className="w-3 h-3" />Refresh status
          </button>
          <span>
            Payments processed securely by Square. Cancel anytime.
          </span>
        </div>
      </main>
    </div>
  );
}
